import React from 'react';
import { ArcPagination, ArcStack, ArcText } from '@src/core/ui';
import { PaginationItem } from '@mui/material';
import type { PaginationRenderItemParams } from '@mui/material/Pagination';
import { BarChart } from '../BarChart';
import { ChartTypeSwitcher, getChartCaptionInterval, getChartIntervalEndDate } from '../ChartTypeSwitcher';
import { PAGE_OFFSET_CENTER, PAGE_OFFSET_COUNT, PAGINATION_BUTTON_SX, CARD_SX } from '../../constants';
import type { AnalyticsChartSectionProps } from './types';

function isDateAfterToday(d: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const other = new Date(d);
  other.setHours(0, 0, 0, 0);
  return other > today;
}

function renderChartIntervalItem(intervalText: string, isNextDisabled: boolean) {
  const RenderItem = (params: PaginationRenderItemParams) => {
    if (params.type === 'page') {
      return <ArcText.caption fontSize={14}>{intervalText}</ArcText.caption>;
    }
    if (params.type === 'start-ellipsis' || params.type === 'end-ellipsis') return null;
    if (params.type === 'next') {
      return <PaginationItem {...params} disabled={isNextDisabled} />;
    }
    return <PaginationItem {...params} />;
  };
  RenderItem.displayName = 'ChartIntervalPaginationItem';
  return RenderItem;
}

export const AnalyticsChartSection: React.FC<AnalyticsChartSectionProps> = ({
  title,
  chartType,
  periodOffset,
  onChartTypeChange,
  onPeriodOffsetChange,
  data,
  barColor = '#9EA0F0',
  valueSuffix,
}) => {
  const isWeekly = chartType === 'weekly';
  const paginationPage = isWeekly ? PAGE_OFFSET_CENTER + periodOffset / 4 : periodOffset + PAGE_OFFSET_CENTER;
  const handlePaginationChange = (_: unknown, page: number) => {
    const delta = isWeekly ? (page - PAGE_OFFSET_CENTER) * 4 : page - PAGE_OFFSET_CENTER;
    onPeriodOffsetChange(delta);
  };

  const intervalText = getChartCaptionInterval(chartType, periodOffset);
  const isNextDisabled = isDateAfterToday(getChartIntervalEndDate(chartType, periodOffset));

  return (
    <ArcStack spacing={3} sx={CARD_SX}>
      <ArcStack direction="row" justifyContent="space-between" alignItems="flex-start">
        <ArcStack spacing={1.5}>
          <ArcText.h5 fontSize={16}>{title}</ArcText.h5>
          <ArcPagination
            size="small"
            page={paginationPage}
            count={PAGE_OFFSET_COUNT}
            siblingCount={0}
            boundaryCount={0}
            sx={PAGINATION_BUTTON_SX}
            renderItem={renderChartIntervalItem(intervalText, isNextDisabled)}
            onChange={handlePaginationChange}
          />
        </ArcStack>
        <ChartTypeSwitcher value={chartType} onChange={onChartTypeChange} />
      </ArcStack>
      <BarChart type={chartType} data={data} barColor={barColor} valueSuffix={valueSuffix} />
    </ArcStack>
  );
};
